/**
 * Cache handlers — bridge between Socket.IO events and the TanStack Query cache.
 *
 * # REST is source of truth — Socket.IO is an accelerator.
 *
 * Decision rule (from §12 master prompt + architecture deep-dive):
 *   - COMPLETE payload → `queryClient.setQueryData(key, data)` (avoids a round-trip)
 *   - HINT only        → `queryClient.invalidateQueries(key)` (REST re-fetches)
 *
 * Every payload is validated with Zod at this boundary. If validation fails the
 * event is logged (without PII) and discarded — it never reaches the cache.
 *
 * Query keys are always obtained via `queryKeys` from `@hostesa/api-client`.
 * Keys are NEVER hand-built here.
 */

import { hotelScopePrefix, queryKeys } from '@hostesa/api-client';
import type { QueryClient } from '@tanstack/react-query';
import {
  bookingChangedEventSchema,
  callHandoffRequestedEventSchema,
  callStatusChangedEventSchema,
  callTranscriptFinalEventSchema,
  knowledgeGapDetectedEventSchema,
  LIVE_EVENTS,
  offerChangedEventSchema,
  reservationChangedEventSchema,
  taskChangedEventSchema,
} from './events';
import type { RealtimeSocket } from './socket';

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Logs an invalid event without emitting any payload data (no PII).
 */
function logInvalid(eventName: string, issues: string): void {
  console.warn('[realtime] invalid event payload — discarding', { event: eventName, issues });
}

// ---------------------------------------------------------------------------
// Per-event cache handlers
// ---------------------------------------------------------------------------

function handleCallStatusChanged(queryClient: QueryClient, raw: unknown): void {
  const result = callStatusChangedEventSchema.safeParse(raw);
  if (!result.success) {
    logInvalid(LIVE_EVENTS.CALL_STATUS_CHANGED, result.error.message);
    return;
  }
  const { tenantId, hotelId, callId } = result.data;
  // Status is a hint — the full call shape isn't in the payload. Invalidate so
  // REST re-fetches the canonical call record.
  void queryClient.invalidateQueries({
    queryKey: queryKeys.call(tenantId, hotelId, callId),
  });
  // Also invalidate the calls list so the status chip refreshes. The prefix
  // key matches every filter variant of the list (`calls(t, h, filters)` ends
  // in `filters ?? null`, so the unfiltered key is NOT a prefix of a filtered
  // one — pages query with filters like `{ limit: 50 }`).
  void queryClient.invalidateQueries({
    queryKey: queryKeys.callsPrefix(tenantId, hotelId),
  });
  // No dedicated tool-call event exists; a status change is the closest signal
  // that the AI may have taken a new gateway action, so piggyback a hint here to
  // keep the tool-call timeline reasonably fresh (REST re-fetches on view).
  void queryClient.invalidateQueries({
    queryKey: queryKeys.callToolCalls(tenantId, hotelId, callId),
  });
}

function handleCallHandoffRequested(queryClient: QueryClient, raw: unknown): void {
  const result = callHandoffRequestedEventSchema.safeParse(raw);
  if (!result.success) {
    logInvalid(LIVE_EVENTS.CALL_HANDOFF_REQUESTED, result.error.message);
    return;
  }
  const { tenantId, hotelId, callId } = result.data;
  // Hint only — invalidate the call record to surface the handoff state.
  void queryClient.invalidateQueries({
    queryKey: queryKeys.call(tenantId, hotelId, callId),
  });
}

function handleCallTranscriptFinal(queryClient: QueryClient, raw: unknown): void {
  const result = callTranscriptFinalEventSchema.safeParse(raw);
  if (!result.success) {
    logInvalid(LIVE_EVENTS.CALL_TRANSCRIPT_FINAL, result.error.message);
    return;
  }
  const { tenantId, hotelId, callId, data } = result.data;
  // Transcript segment is complete and canonical — append directly to the cache.
  // The transcript key stores an array of segments; append this segment if the
  // key is already populated; otherwise invalidate so the initial fetch runs.
  const key = queryKeys.callTranscript(tenantId, hotelId, callId);
  const existing = queryClient.getQueryData<Array<typeof data>>(key);
  if (existing !== undefined) {
    // Append idempotently (by segmentId) to avoid duplicate segments.
    const alreadyPresent = existing.some((s) => s.segmentId === data.segmentId);
    if (!alreadyPresent) {
      queryClient.setQueryData<Array<typeof data>>(key, [...existing, data]);
    }
  } else {
    // Transcript not yet fetched — let TanStack Query fetch on its own schedule.
    void queryClient.invalidateQueries({ queryKey: key });
  }
}

function handleReservationChanged(queryClient: QueryClient, raw: unknown): void {
  const result = reservationChangedEventSchema.safeParse(raw);
  if (!result.success) {
    logInvalid(LIVE_EVENTS.RESERVATION_CHANGED, result.error.message);
    return;
  }
  const { tenantId, hotelId, data } = result.data;
  // Hint — invalidate the specific reservation and the list.
  void queryClient.invalidateQueries({
    queryKey: queryKeys.reservation(tenantId, hotelId, data.id),
  });
  void queryClient.invalidateQueries({
    queryKey: queryKeys.reservationsPrefix(tenantId, hotelId),
  });
}

function handleOfferChanged(queryClient: QueryClient, raw: unknown): void {
  const result = offerChangedEventSchema.safeParse(raw);
  if (!result.success) {
    logInvalid(LIVE_EVENTS.OFFER_CHANGED, result.error.message);
    return;
  }
  // Offers are part of bookings in the query key model — invalidate bookings.
  const { tenantId, hotelId } = result.data;
  void queryClient.invalidateQueries({
    queryKey: queryKeys.bookingsPrefix(tenantId, hotelId),
  });
}

function handleBookingChanged(queryClient: QueryClient, raw: unknown): void {
  const result = bookingChangedEventSchema.safeParse(raw);
  if (!result.success) {
    logInvalid(LIVE_EVENTS.BOOKING_CHANGED, result.error.message);
    return;
  }
  const { tenantId, hotelId, data } = result.data;
  void queryClient.invalidateQueries({
    queryKey: queryKeys.booking(tenantId, hotelId, data.id),
  });
  void queryClient.invalidateQueries({
    queryKey: queryKeys.bookingsPrefix(tenantId, hotelId),
  });
}

function handleTaskChanged(queryClient: QueryClient, raw: unknown): void {
  const result = taskChangedEventSchema.safeParse(raw);
  if (!result.success) {
    logInvalid(LIVE_EVENTS.TASK_CHANGED, result.error.message);
    return;
  }
  const { tenantId, hotelId, data } = result.data;
  void queryClient.invalidateQueries({
    queryKey: queryKeys.task(tenantId, hotelId, data.id),
  });
  void queryClient.invalidateQueries({
    queryKey: queryKeys.tasksPrefix(tenantId, hotelId),
  });
}

function handleKnowledgeGapDetected(queryClient: QueryClient, raw: unknown): void {
  const result = knowledgeGapDetectedEventSchema.safeParse(raw);
  if (!result.success) {
    logInvalid(LIVE_EVENTS.KNOWLEDGE_GAP_DETECTED, result.error.message);
    return;
  }
  const { tenantId, hotelId } = result.data;
  void queryClient.invalidateQueries({
    queryKey: queryKeys.knowledgeGapsPrefix(tenantId, hotelId),
  });
}

// ---------------------------------------------------------------------------
// Public: register all handlers on a socket
// ---------------------------------------------------------------------------

/**
 * Attaches all cache-update handlers to `socket`. Call once after the socket
 * is created; handlers remain active for the socket's lifetime.
 *
 * All handlers validate payloads with Zod before touching the cache.
 */
export function registerCacheHandlers(
  socket: RealtimeSocket,
  queryClient: QueryClient,
): () => void {
  const handlers: Array<[string, (raw: unknown) => void]> = [
    [LIVE_EVENTS.CALL_STATUS_CHANGED, (raw) => handleCallStatusChanged(queryClient, raw)],
    [LIVE_EVENTS.CALL_HANDOFF_REQUESTED, (raw) => handleCallHandoffRequested(queryClient, raw)],
    [LIVE_EVENTS.CALL_TRANSCRIPT_FINAL, (raw) => handleCallTranscriptFinal(queryClient, raw)],
    [LIVE_EVENTS.RESERVATION_CHANGED, (raw) => handleReservationChanged(queryClient, raw)],
    [LIVE_EVENTS.OFFER_CHANGED, (raw) => handleOfferChanged(queryClient, raw)],
    [LIVE_EVENTS.BOOKING_CHANGED, (raw) => handleBookingChanged(queryClient, raw)],
    [LIVE_EVENTS.TASK_CHANGED, (raw) => handleTaskChanged(queryClient, raw)],
    [LIVE_EVENTS.KNOWLEDGE_GAP_DETECTED, (raw) => handleKnowledgeGapDetected(queryClient, raw)],
  ];

  for (const [event, handler] of handlers) {
    socket.on(event, handler);
  }

  return () => {
    for (const [event, handler] of handlers) {
      socket.off(event, handler);
    }
  };
}

// ---------------------------------------------------------------------------
// Re-export invalidation helper used by reconnect
// ---------------------------------------------------------------------------

/**
 * Invalidates all queries in the scope of a hotel. Used on reconnect to force
 * a full resync after a potential gap in event delivery.
 */
export function invalidateHotelScope(
  queryClient: QueryClient,
  tenantId: string,
  hotelId: string,
): void {
  void queryClient.invalidateQueries({
    queryKey: hotelScopePrefix(tenantId, hotelId),
  });
}
